import React, {
  useContext, useEffect, useReducer, useState,
} from 'react';
import {
  Link, Redirect, useHistory, useLocation, useParams,
} from 'react-router-dom';
import {
  AddCircleOutlineRounded, DownloadForOffline, Save, Undo,
} from '@mui/icons-material';
import {
  Button, Card, CardActions, CardContent, CardHeader,
} from '@mui/material';
import Box from '@mui/material/Box';
import {
  addVoucherToBatch,
  createVoucherPdf,
  deleteVoucherBatch,
  getVoucherBatch,
  getVouchersInBatch,
  updateVoucherBatch,
} from 'shared/api/vouchers';
import {
  dateString, modelMap, priceToString, wrapList,
} from 'shared/utils';
import { BUTTON_COLORS, VARIANTS, VOUCHER_TYPES } from 'shared/constants';
import { InfoModal, Title, TooltipIconButton } from 'shared/components/abstract/interactables';
import { DataFormatter } from 'shared/components/data-display/DataFormatter';
import { SideNavContext } from 'shared/hoc/withSideNav';
import { useRequest } from 'shared/hooks';
import { StripePriceEditor, TextAreaEditor } from 'shared/components/data-display/editors';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { getVoucherBatchPath, viewPath } from 'shared/routes/voucher';
import VoucherButton from './VoucherButton';

export default function VoucherBatch() {
  const syncSideNav = useContext(SideNavContext);
  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const [updateCount, setUpdateCount] = useState(0);
  const { request, isLoading } = useRequest();
  const { batchId } = useParams();
  const { search } = useLocation();
  const history = useHistory();

  const forceUpdate = () => setUpdateCount(updateCount + 1);
  const { data, dirty, updatedData } = state;

  useEffect(() => {
    (async () => {
      const [voucherBatchResult, vouchersResult] = await Promise.all([
        request(getVoucherBatch(batchId)),
        request(getVouchersInBatch(batchId)),
      ]);
      if (voucherBatchResult === undefined || vouchersResult === undefined) return;
      if (!voucherBatchResult || !vouchersResult) {
        dispatch({ action: DataReducerActions.set, payload: null });
        return;
      }
      dispatch({
        action: DataReducerActions.set,
        payload: {
          voucherBatch: voucherBatchResult,
          vouchers: vouchersResult ? vouchersResult.rows : [],
        },
      });
    })();
  }, [request, batchId, updateCount]);

  useEffect(() => {
    if (data && data.voucherBatch) {
      const id = data.voucherBatch.batchId;
      syncSideNav(getVoucherBatchPath(id), `Batch #${id}`);
    }
  }, [data, syncSideNav]);

  if (data === undefined) return null;
  if (data === null) return <Redirect to={viewPath} />;

  const { voucherBatch, vouchers } = data;

  const addNewVoucher = async () => {
    const result = await request(addVoucherToBatch(batchId), 'Added voucher to batch');
    if (!result) return;
    forceUpdate();
  };

  const onSave = async () => {
    const result = await request(updateVoucherBatch(batchId, updatedData), 'Updated batch');
    if (!result) return null;
    dispatch({ action: DataReducerActions.set, payload: { voucherBatch: result, vouchers } });
    return result;
  };

  const resetChanges = () => {
    dispatch({ action: DataReducerActions.clear });
  };

  const deleteVouchers = async () => {
    const result = await request(deleteVoucherBatch(batchId), 'Deleted unused vouchers');
    if (!result) return;

    if (vouchers.filter((v) => v.subscription !== null).length > 0) {
      // The batch had some vouchers redeemed so it still exists.
      forceUpdate();
    } else {
      // Batch didn't have any redemptions so it's deleted.
      syncSideNav(getVoucherBatchPath(batchId));
      dispatch({ action: DataReducerActions.set, payload: null });
    }
  };

  const downloadPDF = () => {
    request(createVoucherPdf(batchId));
  };

  const totalBill = vouchers.reduce((sum, current) => sum + current.billInCents, 0);
  const activeVoucher = new URLSearchParams(search).get('voucher');

  const {
    redeemed,
    ruleType,
    total,
    voucherType,
  } = voucherBatch;
  const model = modelMap[ruleType];

  const batchDescriptions = [
    {
      label: `Valid For ${modelMap[ruleType].title}:`,
      formatter: (vb) => <Link to={model.getView(vb[model.idKey])}>{vb[model.nameKey]}</Link>,
    },
    {
      label: 'Applies: ',
      formatter: (vb) => `${vb.productName} service for ${vb.duration}`,
    },
    {
      label: 'Voucher Type',
      formatter: (vb) => (
        vb.voucherType.id === VOUCHER_TYPES.billable.id
          ? <Link to={model.getView(voucherBatch[model.idKey])}>{voucherBatch[model.nameKey]}</Link>
          : VOUCHER_TYPES[voucherType].label),
    },
    {
      label: 'Monthly Billing Price',
      editor: StripePriceEditor,
      editKey: 'price',
      skipWhen: (vb) => vb.voucherType !== 'billable',
      formatter: (vb) => priceToString(vb.price),
    },
    {
      label: 'All Time Bill',
      formatter: () => priceToString(totalBill),
      skipWhen: () => totalBill === 0,
    },
    {
      label: 'Usage',
      formatter: (vb) => `${vb.redeemed}/${vb.total}`,
      actionButton: <TooltipIconButton
        icon={AddCircleOutlineRounded}
        message="Add a new voucher to this batch"
        onClick={addNewVoucher}
        disabled={isLoading}
      />,
    },
    {
      label: 'Created',
      formatter: (vb) => `${dateString(vb.createdAt)} by ${vb.createdBy}`,
    },
    {
      label: 'Description',
      key: 'description',
      editInline: true,
      editor: TextAreaEditor,
    },
  ];

  const setActiveVoucher = (v) => history.push(v ? `?voucher=${v}` : '?');

  return (
    <>
      <Title title={`Voucher Batch #${batchId}`} />
      <Card sx={{ maxWidth: 900 }}>
        <CardHeader title="Service Voucher Batch" />
        <CardContent>
          <DataFormatter
            data={voucherBatch}
            descriptions={batchDescriptions}
            saveChanges={onSave}
            updateValue={(key, value) => dispatch({
              action: DataReducerActions.update,
              payload: { key, value },
            })}
            updatedData={updatedData}
          />
          <Box sx={{
            padding: '10px',
            backgroundColor: 'background.paper',
            borderRadius: '.25rem',
            borderColor: 'borderColor',
            borderStyle: 'solid',
            borderWidth: '1px',
          }}
          >
            <div className="voucher-batch-grid">
              <div className="voucher-grid-header voucher-grid-available">
                <strong>Available</strong>
              </div>
              <div className="voucher-grid-header voucher-grid-active">
                <strong>Active</strong>
              </div>
              <div className="voucher-grid-header voucher-grid-ended">
                <strong>Ended</strong>
              </div>
              {vouchers.map((voucher, i) => (
                <VoucherButton
                  active={activeVoucher === voucher.voucher}
                  changeActiveVoucher={(n) => setActiveVoucher(wrapList(vouchers, i, n).voucher)}
                  container="grid"
                  key={voucher.voucher}
                  setActiveVoucher={setActiveVoucher}
                  voucher={voucher}
                />
              ))}
            </div>
          </Box>
        </CardContent>
        <CardActions sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          {!dirty ? (
            <>
              <Button
                variant={VARIANTS.contained}
                startIcon={<DownloadForOffline />}
                disabled={isLoading || total === redeemed}
                onClick={downloadPDF}
              >
                Export PDF
              </Button>
              <InfoModal
                actionName="Delete"
                description={<p>This will delete the available vouchers. Are you sure?</p>}
                actionCallback={deleteVouchers}
                color={BUTTON_COLORS.error}
                disabled={isLoading || total === redeemed}
              />
            </>
          ) : (
            <>
              <Button
                variant={VARIANTS.outlined}
                startIcon={<Undo />}
                disabled={!dirty}
                onClick={resetChanges}
              >
                Undo
              </Button>
              <Button
                variant={VARIANTS.contained}
                startIcon={<Save />}
                disabled={!dirty}
                onClick={onSave}
              >
                Save
              </Button>
            </>
          )}

        </CardActions>
      </Card>
    </>
  );
}
